import { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Button, OverlayTrigger, Tooltip, Navbar } from "react-bootstrap";
import "../sidebar-stylesheet.css";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import IconToSolve from "../../../components/icon";
import "primereact/resources/themes/bootstrap4-light-blue/theme.css"; // Tema de Base
import "primereact/resources/primereact.min.css";
import TreeContext from "../../../context/treeContext";
import AssetsTree from "./assetsTree";
import ResourcesTree from "./resourcesTree";
import { useMarquee } from "./useMarquee";
import { isHomeRoute } from "./homeRoute";
import { useToken } from "../../../scripts/tokenContext";

function Sidebar({
  toggleSidebar,
  sidebarOpen,
  toggleTreeSidebar,
  treeSidebarOpen,
}) {
  const { t } = useTranslation();
  const { hasPermission } = useToken();
  const location = useLocation();
  const [treeSidebarClass, setTreeSidebarClass] = useState("");
  const [toggleTreeButtonMargin, setToggleTreeButtonMargin] = useState("");
  const {
    setTreeFilter,
    treeFilter,
    setTreeSelected,
    treeSelected,
    treeData,
    resourceScreen,
  } = useContext(TreeContext);
  const shouldHideTree = location.pathname === "/semPermissao";
  // O filtro de ativo — controles e destaque na árvore — só existe na Home.
  const isHomeScreen = isHomeRoute(location.pathname);
  const selectedNodeKey =
    treeSelected && typeof treeSelected === "object" ? treeSelected.key : null;
  const currentFilterKey = treeFilter?.key ?? null;
  const hasPendingFilterSelection =
    !!selectedNodeKey && selectedNodeKey !== currentFilterKey;

  useEffect(() => {
    const handleWindowResize = () => {
      if (window.innerWidth < 768) {
        toggleSidebar(false);
        toggleTreeSidebar(false);
      }
    };

    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [toggleSidebar, toggleTreeSidebar]);

  useEffect(() => {
    const updateTreeSidebarClass = () => {
      if (!sidebarOpen && !treeSidebarOpen) {
        setTreeSidebarClass("collapsed");
        setToggleTreeButtonMargin("");
      } else if (sidebarOpen && !treeSidebarOpen) {
        setTreeSidebarClass("collapsed");
        setToggleTreeButtonMargin("sidebar-collapsed");
      } else if (!sidebarOpen && treeSidebarOpen) {
        setTreeSidebarClass("");
        setToggleTreeButtonMargin("sidebar-collapsed");
      } else {
        setTreeSidebarClass("");
        setToggleTreeButtonMargin("");
      }
    };

    updateTreeSidebarClass();
  }, [sidebarOpen, treeSidebarOpen]);

  const isActive = (path) => {
    if (path === "/home/eventos") {
      return (
        location.pathname === "/home/eventos" ||
        location.pathname === "/home/alarmes" ||
        location.pathname === "/home/historico"
      );
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const handleLinkClick = () => {
    if (
      (window.innerWidth < 768 && sidebarOpen) ||
      (window.innerWidth < 768 && treeSidebarOpen)
    ) {
      toggleSidebar(false);
      toggleTreeSidebar(false);
    }
  };

  const routeNames = {
    "/home/eventos": t("Home"),
    "/arvore": t("Ativos"),
    "/recursos": t("Recursos"),
    "/usuarios": t("Usuários"),
  };

  // Função para aplicar filtro do nó selecionado
  const handleApplyFilter = () => {
    if (treeSelected) {
      // Captura o caminho (labels dos ancestrais) AGORA, enquanto a árvore está
      // expandida até o nó selecionado. Guardado no próprio filtro, o breadcrumb
      // do chip sobrevive a remontagens da árvore (ex.: sair da Home e voltar,
      // quando a treeData volta colapsada) — antes o caminho era recomputado da
      // treeData carregada e degradava para só o rótulo da folha.
      const pathLabels = findAncestorLabels(treeData, treeSelected.key) || [];
      setTreeFilter({ ...treeSelected, pathLabels }); // nó inteiro (incl. key) + caminho
      setTreeSelected(null);
    }
  };

  // Função para restaurar filtro padrão do usuário
  const handleResetFilter = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const client = user?.user?.client;
    if (client) {
      setTreeFilter({
        key: client.code,
        label: client?.name
          ? `${client?.name} (${client?.code})`
          : client?.code,
        data: client,
      });
    } else {
      setTreeFilter(null);
    }
    setTreeSelected(null);
  };

  // Verifica se o filtro atual é o padrão do usuário
  const isDefaultFilter = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const client = user?.user?.client;
    const result =
      (!treeFilter && client) ||
      (treeFilter && client && treeFilter.key === client.code);
    return result;
  };
  // Filtro ativo e distinto do padrão do usuário: é o que a "pílula" persistente
  // exibe e o que o × remove. É também o gatilho para o botão "Substituir filtro".
  const hasNonDefaultFilter = !!treeFilter && !isDefaultFilter();
  const applyFilterTitle = hasNonDefaultFilter
    ? t("Substituir filtro")
    : t("Filtrar pelo nó Selecionado");
  // Caminho completo (breadcrumb) do nó filtrado. Preferimos o trail salvo no
  // filtro no momento do apply (`pathLabels`), que sobrevive a remontagens da
  // árvore; se ausente (ex.: filtro padrão do cliente), recomputa da treeData
  // carregada e, por fim, degrada para só o próprio rótulo (label).
  const filterAncestorLabels =
    hasNonDefaultFilter && treeFilter
      ? (treeFilter.pathLabels ??
        findAncestorLabels(treeData, treeFilter.key) ??
        [])
      : [];
  const filterPathText =
    hasNonDefaultFilter && treeFilter
      ? [...filterAncestorLabels, treeFilter.label].join(" › ")
      : "";

  return (
    <>
      <Navbar className={`col-1 sidebar ${sidebarOpen ? "" : "collapsed"}`}>
        <Navbar.Collapse id="sidebar-nav">
          <br />
          <div className="sidebar-texto">
            <div className="sidebar-filhos">
              {Object.entries(routeNames).map(([path, name]) => {
                const permissionMap = {
                  "/home/eventos": null,
                  "/arvore": null,
                  "/recursos": null,
                  "/usuarios": { slug: "front_users" },
                };

                const permission = permissionMap[path];

                if (permission && !hasPermission(permission.slug)) {
                  return null; // não renderiza sem permissão
                }

                return (
                  <div
                    className={`sidebar-filho ${isActive(path) ? "pagatual-icn" : ""}`}
                    key={path}
                  >
                    <Link to={path} className="rota" onClick={handleLinkClick}>
                      <div className="d-flex align-items-center">
                        <IconToSolve
                          color="#FAFAFA"
                          iconName={getIconName(path)}
                          width={36}
                          height={36}
                        />
                        <div className="sidebar-log">{name}</div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="sidebar-filhos-recolhido">
            {Object.keys(routeNames).map((path) => (
              <OverlayTrigger
                key={path}
                placement="right"
                delay={{ show: 10 }}
                overlay={
                  <Tooltip id={`tooltip-${path}`}>{routeNames[path]}</Tooltip>
                }
              >
                <div
                  className={`${
                    sidebarOpen ? "hide" : "btn-menu-recolhido"
                  } ${isActive(path) ? "pagatual-icn" : ""}`}
                >
                  <Link to={path} onClick={handleLinkClick}>
                    <IconToSolve
                      className={`${sidebarOpen ? "hide" : "show"}`}
                      color="#FAFAFA"
                      iconName={getIconName(path)}
                      width={36}
                      height={36}
                    />
                  </Link>
                </div>
              </OverlayTrigger>
            ))}
          </div>
          <Button
            title={t("Fechar/Abrir sidebar")}
            onClick={() => {
              toggleSidebar();
            }}
            className={`toggle-sidebar-button btn-menu-fechado collapsed${
              sidebarOpen ? "hide" : ""
            }`}
          >
            <i
              className={`bi btn-menu-icn ${
                sidebarOpen ? "bi-caret-left" : "bi-caret-right"
              }`}
            ></i>
          </Button>
        </Navbar.Collapse>
      </Navbar>
      <Navbar className={`col-2 tree-sidebar ${treeSidebarClass}`}>
        <Navbar.Collapse id="sidebar-nav">
          <div
            className={`tree-sidebar-texto ${treeSidebarOpen ? "" : "collapsed"}`}
          >
            {shouldHideTree ? (
              <div className="tree-sidebar-titulo">
                <div className="h6 mb-0 tree-rota">
                  <span>{t("Sem permissão para página")}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="tree-sidebar-titulo">
                  {Object.entries(routeNames).map(([path, name]) =>
                    isActive(path) ? (
                      <div
                        key={path}
                        className="d-flex align-items-center h6 mb-0 tree-rota"
                      >
                        <IconToSolve
                          color="#FAFAFA"
                          iconName={getIconName(path)}
                          width={36}
                          height={36}
                        />
                        <span className="ms-1">{name}</span>
                        <div style={{ flex: 1 }} />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            marginRight: 8,
                            position: "relative",
                          }}
                        >
                          {/* O botão de aplicar/substituir só existe na Home,
                              que é onde o filtro atua. O "remover filtro" não
                              vive mais aqui: mudou para o × da pílula
                              persistente abaixo, para não oscilar com este
                              botão. */}
                          {isHomeScreen && (
                            <>
                              {/* Botão para aplicar (ou substituir) o filtro do
                                  nó selecionado. Só aparece quando há um nó
                                  selecionado diferente do filtro atual. */}
                              {hasPendingFilterSelection && (
                                <Button
                                  title={applyFilterTitle}
                                  className="p-0"
                                  onClick={handleApplyFilter}
                                  style={{
                                    height: 36,
                                    width: 36,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconToSolve
                                    iconName="FunilQuadrado0"
                                    width={36}
                                    height={36}
                                    color="#FAFAFA"
                                  />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ) : null,
                  )}
                  {/* Pílula persistente do filtro ativo: indicador do que está
                      filtrado, visível em toda a Home, e único lar do "remover
                      filtro" (×). Some ao voltar ao padrão do usuário e fora da
                      Home, onde o filtro não atua. */}
                  {isHomeScreen && hasNonDefaultFilter && (
                    <FilterBreadcrumb
                      prefixIcon={
                        <IconToSolve
                          iconName="FunilQuadrado0"
                          width={20}
                          height={20}
                          color="#FAFAFA"
                        />
                      }
                      ancestorsText={
                        filterAncestorLabels.length > 0
                          ? `${filterAncestorLabels.join(" › ")} ›`
                          : ""
                      }
                      leafLabel={treeFilter.label}
                      fullTitle={`${t("Filtrando")}: ${filterPathText}`}
                      removeTitle={t("Remover filtro")}
                      onRemove={handleResetFilter}
                    />
                  )}
                </div>
                {resourceScreen ? (
                  <ResourcesTree />
                ) : (
                  // A árvore de Ativos é a mesma nas duas telas; só na Home ela
                  // reflete o filtro. A rota chega por prop porque o Sidebar já
                  // a conhece, e assim a árvore não passa a depender do Router.
                  <AssetsTree showFilterHighlight={isHomeScreen} />
                )}
              </>
            )}
          </div>
          <Button
            title={t("Fechar/Abrir sidebar da árvore")}
            onClick={() => {
              toggleTreeSidebar();
            }}
            className={`toggle-tree-sidebar-button tree-btn-menu-fechado collapsed${
              treeSidebarOpen ? "hide" : ""
            } ${toggleTreeButtonMargin}`}
          >
            <i
              className={`bi tree-btn-menu-icn ${
                treeSidebarOpen ? "bi-caret-left" : "bi-caret-right"
              }`}
            ></i>
          </Button>
        </Navbar.Collapse>
      </Navbar>
    </>
  );

  function getIconName(path) {
    switch (path) {
      case "/home/eventos":
        return "Home";
      case "/arvore":
        return "Grupos";
      case "/recursos":
        return "Manutencao";
      case "/usuarios":
        return "Usuario";
      /* istanbul ignore next -- defensive: getIconName is only ever called with the four enumerated routeNames keys (closed domain), so the default is unreachable */
      default:
        return "";
    }
  }
}
Sidebar.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  toggleTreeSidebar: PropTypes.func.isRequired,
  treeSidebarOpen: PropTypes.bool.isRequired,
};

// Pílula (breadcrumb) do filtro ativo, em UMA linha. A folha (nível selecionado)
// é o foco: tem prioridade de espaço — a trilha de ancestrais é que cede primeiro
// (encolhe até virar só "…"), então a folha aparece inteira sempre que couber. Se
// nem assim couber, a própria folha entra no MESMO marquee (…"" em repouso, rola e
// para no hover). O × ("remover filtro") fica fixo à direita, sempre na barra.
function FilterBreadcrumb({
  prefixIcon,
  ancestorsText,
  leafLabel,
  fullTitle,
  removeTitle,
  onRemove,
}) {
  // Marquee de velocidade constante (hook compartilhado) para ancestrais e folha.
  const ancestors = useMarquee(ancestorsText);
  const leaf = useMarquee(leafLabel);

  return (
    <div className="tree-filter-chip" title={fullTitle}>
      <span className="tree-filter-chip-prefix">{prefixIcon}</span>
      {ancestorsText && (
        <span
          ref={ancestors.outerRef}
          className={`tree-filter-chip-ancestors${ancestors.marquee.overflow ? " is-overflow" : ""}`}
        >
          <span
            ref={ancestors.innerRef}
            className="tree-filter-chip-ancestors-inner"
            style={ancestors.marqueeVars}
          >
            {ancestorsText}
          </span>
        </span>
      )}
      <span
        ref={leaf.outerRef}
        className={`tree-filter-chip-leaf${leaf.marquee.overflow ? " is-overflow" : ""}`}
      >
        <span
          ref={leaf.innerRef}
          className="tree-filter-chip-leaf-inner"
          style={leaf.marqueeVars}
        >
          {leafLabel}
        </span>
      </span>
      <button
        type="button"
        className="tree-filter-chip-remove"
        title={removeTitle}
        aria-label={removeTitle}
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  );
}
FilterBreadcrumb.propTypes = {
  prefixIcon: PropTypes.node,
  ancestorsText: PropTypes.string,
  leafLabel: PropTypes.string,
  fullTitle: PropTypes.string,
  removeTitle: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// Retorna os rótulos dos ancestrais (raiz -> pai) do nó com a key dada,
// percorrendo a treeData já carregada. [] quando o nó é raiz (sem ancestrais) e
// null quando o nó não está carregado — assim o chamador degrada para só o
// rótulo do próprio nó, sem quebrar strings de key nem forçar buscas.
function findAncestorLabels(nodes, key, trail = []) {
  if (!nodes || !key) return null;
  for (const node of nodes) {
    if (node.key === key) return trail;
    if (node.children) {
      const found = findAncestorLabels(node.children, key, [
        ...trail,
        node.label,
      ]);
      if (found) return found;
    }
  }
  return null;
}

export default Sidebar;
